import { ItemStatuses } from 'constants/statuses';

// Some properties can satisfy a single logical onboarding step (e.g. "renters
// insurance") through two different items — a generic resident-onboarding
// item and a separate, more specific item (e.g. Assurant's `insurance` item)
// — but only one of the two is ever actually written to depending on which
// flow the resident used. Rank statuses by how far along they represent so
// callers can tell, for a given pair of linked items, which one reflects
// real progress.
const STATUS_PROGRESS_RANK: Partial<Record<ItemStatuses, number>> = {
  [ItemStatuses.NOT_STARTED]: 0,
  [ItemStatuses.STARTED]: 0,
  [ItemStatuses.SCRUBBING]: 1,
  [ItemStatuses.PENDING]: 1,
  [ItemStatuses.UNVERIFIED]: 1,
  [ItemStatuses.VALIDATING]: 1,
  [ItemStatuses.INFO_REQUESTED]: 1,
  [ItemStatuses.INFORMATION_REQUESTED]: 1,
  [ItemStatuses.COMPLETED]: 2,
  [ItemStatuses.VALIDATED_AND_COMPLETED]: 2,
  // Treated as terminal/resolved, same tier as COMPLETED, so a cancelled
  // item on one side still lets the other item's real progress win ties
  // rather than getting stuck comparing against "not started".
  [ItemStatuses.CANCELLED]: 2,
};

// Unranked/unknown statuses (including undefined/null, e.g. no item exists
// yet) are treated as the least advanced, so any recognised status wins.
export const getStatusProgressRank = (
  status: string | null | undefined
): number => STATUS_PROGRESS_RANK[status as ItemStatuses] ?? -1;

// Returns whichever of the two statuses represents further progress. Ties
// (including both undefined) favor `status`.
export const getFurtherAlongStatus = (
  status: string | null | undefined,
  otherStatus: string | null | undefined
): string | undefined =>
  (getStatusProgressRank(otherStatus) > getStatusProgressRank(status)
    ? otherStatus
    : status) ?? undefined;
